import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { PhoneCall, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePhoneBar } from "@/contexts/PhoneBarContext";

interface ContactFormProps {
  cityName?: string;
  serviceName?: string;
}

export const ContactForm = ({ cityName = "Long Island", serviceName = "Roofing" }: ContactFormProps) => {
  const { toast } = useToast();
  const { phoneNumber } = usePhoneBar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-contact-form', {
        body: formData
      });

      if (error) {
        console.error('Submission error:', error);
        toast({
          title: "Submission Failed",
          description: "There was an error submitting your form. Please try again or call us directly.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Thank You!",
        description: data?.message || "We'll contact you within 24 hours for your free estimate.",
      });

      // Reset form
      setFormData({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: "Submission Failed",
        description: `Please try again or call us directly at ${phoneNumber.replace('+1', '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}.`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card className="w-full max-w-md mx-auto bg-card/95 backdrop-blur-sm shadow-lg border-0">
      <CardHeader className="text-center pb-4">
        <h2 className="text-xl font-bold text-card-foreground">
          Get Your Free {serviceName} Estimate
        </h2>
        <p className="text-sm text-muted-foreground">
          Professional {serviceName.toLowerCase()} services in {cityName}. Call {phoneNumber.replace('+1', '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')} today!
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Full Name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            required
            className="border-border/50 focus:border-primary"
            aria-label="Full Name"
          />
          <Input
            placeholder="Phone Number"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            required
            className="border-border/50 focus:border-primary"
            aria-label="Phone Number"
          />
          <Input
            placeholder="Email Address"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            required
            className="border-border/50 focus:border-primary"
            aria-label="Email Address"
          />
          <Select value={formData.service} onValueChange={(value) => handleInputChange("service", value)}>
            <SelectTrigger className="border-border/50 focus:border-primary" aria-label="Select a service">
              <SelectValue placeholder="Select Service" />
            </SelectTrigger>
            <SelectContent className="bg-card border border-border">
              <SelectItem value="residential-roofing">Residential Roof Repair & Replacement</SelectItem>
              <SelectItem value="storm-damage">Storm Damage & Insurance Claims</SelectItem>
              <SelectItem value="commercial">Commercial & Multi-Family Roofing</SelectItem>
              <SelectItem value="siding">Siding Installation & Repair</SelectItem>
              <SelectItem value="windows">Window Replacement</SelectItem>
              <SelectItem value="chimney">Chimney Repair & Maintenance</SelectItem>
              <SelectItem value="gutters">Gutter Installation & Cleaning</SelectItem>
              <SelectItem value="emergency">Emergency Roof Repair</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-primary hover:bg-primary-dark text-primary-foreground font-semibold py-3 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
            aria-label="Submit form for free roofing estimate"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              "Get Free Estimate Now"
            )}
          </Button>
        </form>
        
        <div className="flex items-center justify-center pt-4 border-t border-border/50">
          <Button
            variant="outline"
            size="lg"
            className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold transition-all duration-200"
            onClick={() => window.location.href = `tel:${phoneNumber}`}
          >
            <PhoneCall className="w-4 h-4 mr-2" />
            {phoneNumber.replace('+1', '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};